import { HttpBackend, HttpClient } from '@angular/common/http';
import { catchError, EMPTY, map, Observable, of, ReplaySubject, shareReplay } from 'rxjs';
import { Injectable } from '@angular/core';
import { EnvService } from 'app/core/services/env.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly TOKEN_LOCATION_KEY = 'location';
  private readonly TOKEN_STORAGE_KEY = 'accessToken';
  private readonly TOKEN_VALUE_KEY = 'stash_token';
  private readonly http: HttpClient;
  private readonly apiToken$: Observable<string>;
  private readonly sessionExpired$ = new ReplaySubject<void>(1);
  private sessionExpiredEmitted = false;

  constructor(
    private readonly envService: EnvService,
    handler: HttpBackend
  ) {
    this.http = new HttpClient(handler);
    this.apiToken$ = this.retrieveToken().pipe(shareReplay(1));
  }

  clearToken(): void {
    localStorage.removeItem(this.TOKEN_STORAGE_KEY);
  }

  hasToken(): boolean {
    const token = localStorage.getItem(this.TOKEN_STORAGE_KEY);
    return !!token && !this.isTokenExpired(token);
  }

  // JWT payloads are base64url-encoded (RFC 7515): '+' → '-', '/' → '_', no padding.
  // atob() only handles standard base64 and throws on '-' or '_', so normalise first.
  private decodeJwtPayload(token: string): Record<string, unknown> {
    const seg = token.split('.')[1] ?? '';
    const b64 = seg.replaceAll('-', '+').replaceAll('_', '/');
    return JSON.parse(atob(b64)) as Record<string, unknown>;
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = this.decodeJwtPayload(token);
      // exp is seconds since epoch; multiply by 1000 to compare with Date.now()
      return typeof payload['exp'] === 'number' && (payload['exp'] as number) * 1000 < Date.now();
    } catch {
      return true;
    }
  }

  getApiToken(): Observable<string> {
    return this.apiToken$;
  }

  getSessionExpired$(): Observable<void> {
    return this.sessionExpired$.asObservable();
  }

  handleTokenExpiry(): void {
    this.clearToken();
    if (!this.sessionExpiredEmitted) {
      this.sessionExpiredEmitted = true;
      this.sessionExpired$.next();
    }
  }

  getUserName(): Observable<string> {
    return this.getApiToken().pipe(
      map(apiToken => {
        const claims = this.decodeJwtPayload(apiToken);
        return claims['unique_name'] as string;
      })
    );
  }

  private removeTokenUrlParameter(urlParams: URLSearchParams): void {
    urlParams.delete(this.TOKEN_LOCATION_KEY);
    urlParams.delete(this.TOKEN_VALUE_KEY);
    const newUrl = urlParams.toString()
      ? `${globalThis.location.origin}${globalThis.location.pathname}?${urlParams.toString()}`
      : `${globalThis.location.origin}${globalThis.location.pathname}`;
    globalThis.history.replaceState({}, document.title, newUrl);
  }

  private retrieveToken(): Observable<string> {
    const urlParams = new URLSearchParams(globalThis.location.search);
    const tokenFromUrl = urlParams.get(this.TOKEN_VALUE_KEY);

    if (tokenFromUrl) {
      // TMS-865: clean the URL before the POST so the stash_token is never visible
      // in the address bar regardless of whether the exchange succeeds or fails.
      this.removeTokenUrlParameter(urlParams);

      return this.http.post(this.envService.api.system.login, { stashToken: tokenFromUrl }).pipe(
        map((response: any) => {
          if (!response.accessToken) {
            throw new Error('Failed to retrieve API token');
          }
          localStorage.setItem(this.TOKEN_STORAGE_KEY, response.accessToken);
          return response.accessToken as string;
        }),
        catchError(() => {
          this.handleTokenExpiry();
          return EMPTY;
        })
      );
    }

    const tokenFromStorage = localStorage.getItem(this.TOKEN_STORAGE_KEY);

    if (!tokenFromStorage || this.isTokenExpired(tokenFromStorage)) {
      this.handleTokenExpiry();
      return EMPTY;
    }

    return of(tokenFromStorage);
  }
}
